import { List as ImmutableList } from 'immutable';
import { isGoalReport, isUnifiedAnalyticsReport, isUnifiedEventsReport } from 'reporting-data/tsTypes/reportTypes';
import { getCustomFilters, getFilterGroups, getReportConfig, getDataType } from 'reporting-data/report/configReportGetters';
import { isFiscalYearEnabled } from '../common/isFiscalYearEnabled';
import { setCustomFilters, setFilterGroups } from 'reporting-data/report/configReportSetters';
import { fromJS } from 'immutable';
import { EQ, GTE, GT, ROLLING_DATE_RANGE, BETWEEN, IN, LTE, LT, NEQ, NOT_HAS_PROPERTY, TIME_UNIT_TO_DATE } from 'reporting-data/constants/operators';
import { getRequiredGoalReportEditors, getCTAReportRequiredFilters, isCTAReportWithRequiredFilters, getContactCreateAttributionRequiredFilters } from '../getDynamicReportEditors';
import { getCustomWidgetType } from 'reporting-data/report/reportGetters';
import { isExternalDataType } from 'reporting-data/report/externalReportGetters';
import { CUSTOM_WIDGET_TYPES } from 'reporting-data/tsTypes/customWidgetTypes';
// @ts-expect-error untyped
import { isContactCreateAttributionReport } from 'reporting-action-components/lib/report-logic';
const CUSTOMER_DATA_FILTERS_FISCAL_YEAR_ROLLING_OFFSET = '__hs__FISCAL_YEAR_OFFSET';

/**
 * Supported operators for quick filters
 *
 * Quick filters supports a subset of operators.  In case a filters is added via Advanced Filters, it isn't guaranteed to be supported
 * by quick filters.
 */
const QUICK_FILTERS_SUPPORTED_OPERATORS = {
  enumeration: [IN, NOT_HAS_PROPERTY],
  datetime: [ROLLING_DATE_RANGE, TIME_UNIT_TO_DATE],
  date: [ROLLING_DATE_RANGE, TIME_UNIT_TO_DATE],
  number: [BETWEEN, EQ, NEQ, GT, GTE, LT, LTE],
  string: [EQ],
  bool: [EQ]
};
const readOnlyFilterConditions = [{
  condition: isGoalReport,
  getFilters: getRequiredGoalReportEditors
}, {
  condition: isCTAReportWithRequiredFilters,
  getFilters: getCTAReportRequiredFilters
}, {
  condition: isContactCreateAttributionReport,
  getFilters: getContactCreateAttributionRequiredFilters
}];
export const getReadOnlyFilters = report => {
  var _readOnlyFilterCondit;
  const reportConfig = getReportConfig(report);
  const readOnlyFilters = ((_readOnlyFilterCondit = readOnlyFilterConditions.find(({
    condition
  }) => condition(report))) === null || _readOnlyFilterCondit === void 0 ? void 0 : _readOnlyFilterCondit.getFilters(report)) || [];
  if (readOnlyFilters.length > 0) {
    return getCustomFilters(reportConfig).filter(filter => readOnlyFilters.find(({
      property
    }) => property === filter.get('property')) !== undefined);
  }
  return ImmutableList();
};
const getEditableFilters = report => {
  const reportConfig = getReportConfig(report);
  const customFilters = getCustomFilters(reportConfig);
  const readOnlyFilters = getReadOnlyFilters(report);
  return customFilters.filter(filter => readOnlyFilters.find(readOnlyFilter => readOnlyFilter.get('property') === filter.get('property')) === undefined);
};
const getQuickFilters = (report, quickFilterPropertyNames, userAddedQuickFilterPropertyNames, properties) => {
  const customFilters = getEditableFilters(report);
  const quickFiltersPropertyNames = [...userAddedQuickFilterPropertyNames, ...quickFilterPropertyNames];
  const filters = customFilters.reduce((acc, filter) => {
    const filterProperty = filter.get('property');
    if (acc.find(addedFilter => addedFilter.get('property') === filterProperty)) {
      return acc;
    }
    const isQuickFilter = quickFiltersPropertyNames.includes(filterProperty);
    if (isQuickFilter) {
      var _QUICK_FILTERS_SUPPOR;
      // is Operator supported
      const property = properties.get(filterProperty);
      if (!property) {
        return acc;
      }
      const hasSupportedOperators = (_QUICK_FILTERS_SUPPOR = QUICK_FILTERS_SUPPORTED_OPERATORS[property.get('externalOptions') ? 'enumeration' : property.get('type')]) === null || _QUICK_FILTERS_SUPPOR === void 0 ? void 0 : _QUICK_FILTERS_SUPPOR.includes(filter.get('operator'));
      if (hasSupportedOperators) {
        acc = acc.push(filter);
      }
    }
    return acc;
  }, ImmutableList());
  return filters;
};
export const formatFilterValues = (filters, properties) => {
  return filters.map(filterGroup => {
    const updatedFilters = filterGroup.filters.map(filter => {
      const property = properties.get(filter.property);
      if (!property) {
        return filter;
      }
      const type = property.get('type');
      const value = type === 'number' && typeof filter.value !== 'number' && !isNaN(Number(filter.value)) ? Number(filter.value) : filter.value;
      return Object.assign({}, filter, value ? {
        value
      } : {});
    });
    return {
      filters: updatedFilters
    };
  });
};
export const getFilterValuesFromReport = (report, quickFilterPropertyNames, userAddedQuickFilterPropertyNames, objectTypeId, properties) => {
  const reportConfig = getReportConfig(report);
  let filterGroups = getFilterGroups(reportConfig).toJS();
  if (filterGroups.length === 0) {
    // migrate custom filters to filterGroups
    const customFilters = getEditableFilters(report);
    const quickFilters = getQuickFilters(report, quickFilterPropertyNames, userAddedQuickFilterPropertyNames, properties);
    const customNonQuickFilters = customFilters.filter(filter => quickFilters.find(quickFilter => quickFilter === filter) === undefined);
    filterGroups = customNonQuickFilters.size ? [{
      filters: customNonQuickFilters.toJS()
    }] : [];
  }
  return {
    objectTypeId,
    filterGroups: formatFilterValues(filterGroups, properties)
  };
};
export const getQuickFilterValuesFromReport = (report, quickFilterPropertyNames, userAddedQuickFilterPropertyNames, objectTypeId, properties) => {
  const filters = getQuickFilters(report, quickFilterPropertyNames, userAddedQuickFilterPropertyNames, properties).toJS();
  const filterGroups = filters.length ? [{
    filters
  }] : [];
  return {
    objectTypeId,
    filterGroups: formatFilterValues(filterGroups, properties)
  };
};
export const applyFiscalYear = (report, filters, gates) => {
  if (!filters) {
    return filters;
  }
  const fiscalYearEnabled = isFiscalYearEnabled(report, gates);
  let updatedFiltersWithFiscalYear = filters;
  if (fiscalYearEnabled) {
    updatedFiltersWithFiscalYear = filters.map(filter => {
      if (filter.get('rollingOffset') === CUSTOMER_DATA_FILTERS_FISCAL_YEAR_ROLLING_OFFSET) {
        filter = filter.set('useFiscalYear', true);
      }
      return filter;
    }).toList();
  }
  return updatedFiltersWithFiscalYear;
};
export const formatReportForFilterGroups = (report, quickFilterPropertyNames, userAddedQuickFilterPropertyNames, objectTypeId, properties) => {
  return report.updateIn(['config'], currentConfig => {
    const filterGroupValues = getFilterValuesFromReport(report, quickFilterPropertyNames, userAddedQuickFilterPropertyNames, objectTypeId, properties);
    const quickFilters = getQuickFilterValuesFromReport(report, quickFilterPropertyNames, userAddedQuickFilterPropertyNames, objectTypeId, properties);
    const customFilters = quickFilters !== null && quickFilters !== void 0 && quickFilters.filterGroups.length ? quickFilters === null || quickFilters === void 0 ? void 0 : quickFilters.filterGroups[0].filters : [];
    return setCustomFilters(setFilterGroups(currentConfig, fromJS(filterGroupValues.filterGroups)), fromJS(customFilters));
  });
};
export const getCustomFiltersFromFilterGroups = filterGroup => {
  return filterGroup.filterGroups.length ? filterGroup.filterGroups[0].filters : [];
};
export const getFilterGroupsFromCrmSearchFilter = filterGroup => {
  return filterGroup.filterGroups.length ? filterGroup.filterGroups : [];
};
const OR_FILTERS_DISABLED_CUSTOM_WIDGET_TYPES = [CUSTOM_WIDGET_TYPES.WATERFALL, CUSTOM_WIDGET_TYPES.CHANGE_HISTORY_DEAL_LIST, CUSTOM_WIDGET_TYPES.SALES_QUOTA, CUSTOM_WIDGET_TYPES.PIPELINE_SNAPSHOTS, CUSTOM_WIDGET_TYPES.PERSONA, CUSTOM_WIDGET_TYPES.SALES_QUOTA];
export const areORFiltersSupported = report => {
  const dataType = getDataType(report);
  if (isExternalDataType(dataType) || isUnifiedAnalyticsReport(report) || isUnifiedEventsReport(report)) {
    return false;
  }
  const customWidgtType = getCustomWidgetType(report);
  if (customWidgtType === undefined) {
    return true;
  }
  return !OR_FILTERS_DISABLED_CUSTOM_WIDGET_TYPES.includes(customWidgtType);
};